import { humanizeDistance } from "@components/store-locator/humanize-distance";
import { activeSiteConfig } from "@config/site/site-config";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import { Table, Tbody, Td, Tr } from "@ui/components/data-display/table/Table";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Link } from "@ui/components/navigation/link/Link";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import type { DayOfWeek, Hours, WalkInHours as WalkInHourType } from "./controller";
import { Controller, dayOfWeek } from "./controller";
import { storeLocator } from "@lib/constants/contentful/storeLocator";
import { hoursNotAvailable } from "./utils/hoursNotAvailable";

type WalkInHoursProps = {
    walkInHours: WalkInHourType;
    localDate: Date;
    name: string;
    street: string;
    postalCode: string;
    city: string;
    phone: string;
    isModal?: boolean;
    latitude: number;
    longitude: number;
};

export const WalkInHours = ({
    walkInHours,
    localDate,
    name,
    street,
    postalCode,
    city,
    phone,
    isModal,
    latitude,
    longitude,
}: WalkInHoursProps) => {
    const { locale } = useRouter();
    const { dayOfWeekLabels, clientLocation } = useContext(Controller);
    const { getMultiple } = useMicrocopy();
    const {
        geo: { imperialMetricSystem },
    } = activeSiteConfig;
    const [distance, setDistance] = useState<string>("");

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.defaultWorkingDay,
            ClickAndCollect.storeLocatorDirections,
            storeLocator.hoursNotAvailableExtended,
            storeLocator.hoursNotAvailable,
        ]);
    })();

    const localDayOfWeek = dayOfWeek[localDate.getDay()];

    const checkTime = (time: string) => (time?.trim() !== "" ? time : "00:00");
    const timeRangeOfDay = (hours: Hours) => {
        const closed = "00:00";
        return hours?.open === closed && hours?.close === closed
            ? microcopies[storeLocator.hoursNotAvailable]
            : `${checkTime(hours?.open)} - ${checkTime(hours?.close)}`;
    };

    const generateWeekDays = (startDay: DayOfWeek) => {
        const defaultDayOrder: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
        const startIndex = defaultDayOrder?.indexOf(startDay);
        if (startIndex === -1) return defaultDayOrder;
        return defaultDayOrder.slice(startIndex).concat(defaultDayOrder?.slice(0, startIndex));
    };

    const localDayOrder = generateWeekDays(microcopies[ClickAndCollect.defaultWorkingDay]);

    useEffect(() => {
        if (clientLocation) {
            const dist = humanizeDistance(
                {
                    latitude: clientLocation.lat,
                    longitude: clientLocation.lng,
                },
                {
                    latitude,
                    longitude,
                },
                locale,
                !imperialMetricSystem ? "metric" : "imperial"
            );

            setDistance(dist);
        }
    }, [clientLocation, latitude, locale, longitude]);

    const params = encodeURIComponent([name, street, city, postalCode].join(","));
    const url = `https://www.google.com/maps/dir/?api=1&destination=${params}`;

    return (
        <Flex mt={4} justifyContent="space-between">
            <Table
                sx={{ fontVariantNumeric: "normal" }}
                variant="unstyled"
                width="auto"
                color="gray.500"
            >
                <Tbody>
                    {hoursNotAvailable(walkInHours) ? (
                        <Tr>
                            <Td p={0} pr={10} colSpan={3}>
                                {microcopies[storeLocator.hoursNotAvailableExtended]}
                            </Td>
                        </Tr>
                    ) : (
                        localDayOrder.map((day) => (
                            <Tr key={day}>
                                <Td p={0} lineHeight={isModal && "27px"}>
                                    <Text
                                        {...(day === localDayOfWeek && {
                                            size: "b2",
                                            color: "black",
                                        })}
                                        mb={1}
                                        fontWeight={"bold"}
                                    >
                                        {dayOfWeekLabels[day]}
                                    </Text>
                                </Td>
                                <Td p={0}>
                                    <Text fontWeight={"bold"} mb={1} mx={2} visibility="hidden">
                                        |
                                    </Text>
                                </Td>
                                <Td p={0} lineHeight={isModal && "27px"}>
                                    <Text
                                        {...(day === localDayOfWeek && {
                                            size: "b2",
                                            color: "black",
                                        })}
                                        mb={1}
                                        fontWeight={"bold"}
                                    >
                                        {timeRangeOfDay(walkInHours?.[day])}
                                    </Text>
                                </Td>
                            </Tr>
                        ))
                    )}
                    <Tr>
                        <Td p={0}>
                            <Link href={url} target="_blank" mt={4}>
                                {microcopies[ClickAndCollect.storeLocatorDirections]}
                            </Link>
                        </Td>
                        {phone ? (
                            <Td p={0}>
                                <Text mt={4} mx={2}>
                                    |
                                </Text>
                            </Td>
                        ) : null}
                        {phone ? (
                            <Td p={0}>
                                <Link href={`tel:${phone}`} mt={4}>
                                    {phone}
                                </Link>
                            </Td>
                        ) : null}
                    </Tr>
                </Tbody>
            </Table>

            {distance && (
                <Text sx={{ textWrap: "nowrap" }} size="b2">
                    {distance}
                </Text>
            )}
        </Flex>
    );
};
